import { cn } from "lib/utils";
import type { ReactNode } from "react";

/**
 * 自前定義の badge / tag / status トーン体系。
 *
 * 設計方針:
 *   1. 通常の status は全て soft pastel (低彩度) で一覧画面のノイズを最小化
 *   2. 致命的失敗・容量超過・成約のような重要な瞬間だけ saturated を使う
 *   3. 「順序のある状態」(CRM ステージ等) は violet グラデーション
 *   4. 「中立な分類タグ」は色を持たない neutral
 *   5. DaisyUI の badge-* プリセットは使わない (彩度が強すぎるため)
 */
export type BadgeTone =
  | "soft-success"
  | "soft-warning"
  | "soft-error"
  | "soft-info"
  | "soft-neutral"
  | "critical-error"
  | "critical-success"
  | "stage-1"
  | "stage-2"
  | "stage-3"
  | "stage-4"
  | "stage-final"
  | "tag-neutral";

const BASE =
  "inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap";

export const badgeToneClass: Record<BadgeTone, string> = {
  "soft-success": `${BASE} bg-emerald-50 text-emerald-700 border-emerald-200`,
  "soft-warning": `${BASE} bg-amber-50 text-amber-700 border-amber-200`,
  "soft-error": `${BASE} bg-rose-50 text-rose-700 border-rose-200`,
  "soft-info": `${BASE} bg-sky-50 text-sky-700 border-sky-200`,
  "soft-neutral": `${BASE} bg-slate-100 text-slate-600 border-slate-200`,
  "critical-error": `${BASE} bg-rose-600 text-white border-rose-700`,
  "critical-success": `${BASE} bg-emerald-600 text-white border-emerald-700`,
  "stage-1": `${BASE} bg-violet-50 text-violet-800 border-violet-200`,
  "stage-2": `${BASE} bg-violet-100 text-violet-800 border-violet-300`,
  "stage-3": `${BASE} bg-violet-200 text-violet-900 border-violet-400`,
  "stage-4": `${BASE} bg-violet-400 text-white border-violet-500`,
  "stage-final": `${BASE} bg-violet-700 text-white border-violet-800`,
  "tag-neutral": `${BASE} bg-white text-slate-700 border-slate-300`,
};

/** badge ではなく単なる「丸ドット」用の bg-* クラスのみ */
export const badgeToneDotBg: Record<BadgeTone, string> = {
  "soft-success": "bg-emerald-400",
  "soft-warning": "bg-amber-400",
  "soft-error": "bg-rose-400",
  "soft-info": "bg-sky-400",
  "soft-neutral": "bg-slate-300",
  "critical-error": "bg-rose-600",
  "critical-success": "bg-emerald-600",
  "stage-1": "bg-violet-200",
  "stage-2": "bg-violet-300",
  "stage-3": "bg-violet-400",
  "stage-4": "bg-violet-500",
  "stage-final": "bg-violet-700",
  "tag-neutral": "bg-slate-300",
};

export const badgeToneDescription: Record<BadgeTone, string> = {
  "soft-success": "完了 / 正常 / 有効 / 承認",
  "soft-warning": "保留 / 未完了 / 警告 / 一部失敗",
  "soft-error": "失敗 / 拒否 / バウンス / 期限切れ (通常)",
  "soft-info": "進行中 / 自動 / trial / 通知",
  "soft-neutral": "非アクティブ / 下書き / キャンセル / クローズ / 中立",
  "critical-error": "致命的失敗 / 容量超過 / 緊急対応必要",
  "critical-success": "重要マイルストーン到達 (成約等)",
  "stage-1": "順序ステージ 初期",
  "stage-2": "順序ステージ 中盤前半",
  "stage-3": "順序ステージ 中盤後半",
  "stage-4": "順序ステージ 終盤手前",
  "stage-final": "順序ステージ 最終 (例: オファー)",
  "tag-neutral": "ニュートラル分類タグ (人材/案件/カテゴリ等)",
};

export type StatusBadgeProps = {
  tone: BadgeTone;
  children: ReactNode;
};

/**
 * 全画面で使う status / badge / tag リーフ。tone を受け取って描画する。
 * @param {StatusBadgeProps} props tone と中身
 * @return {JSX.Element} badge 要素
 */
export const StatusBadge = ({
  tone,
  children,
}: StatusBadgeProps): JSX.Element => {
  return <span className={cn(badgeToneClass[tone])}>{children}</span>;
};
